import React, { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import BulkResults from './BulkResults';
import './BulkValidator.css';

const API = process.env.REACT_APP_API_URL || '';
const MAX_EMAILS = 2000;

function downloadTemplate() {
  const csv = 'email\nuser@example.com\ntest@gmail.com\ninfo@company.org\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'email-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export default function BulkValidator() {
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null); // { emailCount, totalRows, duplicateCount, emailKey }
  const [jobId, setJobId]         = useState(null);
  const [phase, setPhase]         = useState('idle'); // idle | uploading | streaming | done | cancelled | error
  const [progress, setProgress]   = useState({ done: 0, total: 0 });
  const [results, setResults]     = useState([]);
  const [summary, setSummary]     = useState(null);
  const [errorMsg, setErrorMsg]   = useState('');
  const esRef   = useRef(null);   // EventSource
  const jobRef  = useRef(null);   // current jobId for cancel

  // ── Dropzone ──────────────────────────────────────────────────────────────
  const onDrop = useCallback((accepted, rejected) => {
    if (rejected.length) {
      setErrorMsg('Only CSV, Excel (.xlsx, .xls), or PDF files are accepted.');
      return;
    }
    if (!accepted.length) return;

    const f = accepted[0];
    setFile(f);
    setErrorMsg('');
    setResults([]);
    setSummary(null);
    setJobId(null);
    setPhase('idle');
    setProgress({ done: 0, total: 0 });

    // Quick client-side preview for CSV
    if (f.name.toLowerCase().endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const lines = e.target.result.split('\n').filter(l => l.trim());
        const dataLines = Math.max(0, lines.length - 1);
        setPreview({ emailCount: dataLines, totalRows: dataLines, duplicateCount: null, emailKey: null });
      };
      reader.readAsText(f);
    } else {
      setPreview(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  // ── Upload file → get jobId ───────────────────────────────────────────────
  const handleValidate = async () => {
    if (!file) return;
    setErrorMsg('');
    setResults([]);
    setSummary(null);
    setPhase('uploading');
    setProgress({ done: 0, total: 0 });

    const formData = new FormData();
    formData.append('file', file);

    let uploadData;
    try {
      const res = await fetch(`${API}/api/validate-bulk/upload`, {
        method: 'POST',
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) {
        setErrorMsg(json.error || 'Upload failed.');
        setPhase('error');
        return;
      }
      uploadData = json;
    } catch (err) {
      setErrorMsg('Upload failed: ' + err.message);
      setPhase('error');
      return;
    }

    const { jobId: jid, total, totalRows, duplicateCount, emailKey } = uploadData;
    setJobId(jid);
    jobRef.current = jid;
    setPreview({ emailCount: total, totalRows, duplicateCount, emailKey });
    setProgress({ done: 0, total });
    setPhase('streaming');

    // ── Open SSE stream ───────────────────────────────────────────────────
    const es = new EventSource(`${API}/api/validate-bulk/stream/${jid}`);
    esRef.current = es;

    es.addEventListener('start', (e) => {
      const d = JSON.parse(e.data);
      setProgress({ done: 0, total: d.total });
    });

    es.addEventListener('init', (e) => {
      const d = JSON.parse(e.data);
      setResults(d.results || []);
      setProgress({ done: d.processed, total: d.total });
      if (d.complete || d.cancelled) {
        es.close();
        setPhase(d.cancelled ? 'cancelled' : 'done');
      }
    });

    es.addEventListener('result', (e) => {
      const d = JSON.parse(e.data);
      setResults(prev => [...prev, ...(d.results || [])]);
      setProgress({ done: d.processed, total: d.total });
    });

    es.addEventListener('complete', (e) => {
      const d = JSON.parse(e.data);
      setSummary(d.summary || null);
      setProgress({ done: d.processed, total: d.total });
      setPhase(d.cancelled ? 'cancelled' : 'done');
      es.close();
      esRef.current = null;
    });

    es.addEventListener('cancelled', (e) => {
      const d = JSON.parse(e.data);
      setProgress({ done: d.processed, total: d.total });
      setPhase('cancelled');
      es.close();
      esRef.current = null;
    });

    es.onerror = () => {
      // Only treat as error if we haven't finished
      setPhase(p => {
        if (p === 'streaming') {
          setErrorMsg('Connection lost. Results so far are shown below.');
          return 'error';
        }
        return p;
      });
      es.close();
      esRef.current = null;
    };
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    const jid = jobRef.current;
    if (jid) {
      try {
        await fetch(`${API}/api/validate-bulk/cancel/${jid}`, { method: 'POST' });
      } catch (_) {}
    }
    setPhase('cancelled');
  };

  // ── Clear ─────────────────────────────────────────────────────────────────
  const handleClear = () => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setFile(null);
    setPreview(null);
    setJobId(null);
    setPhase('idle');
    setProgress({ done: 0, total: 0 });
    setResults([]);
    setSummary(null);
    setErrorMsg('');
    jobRef.current = null;
  };

  const isRunning   = phase === 'uploading' || phase === 'streaming';
  const progressPct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const showResults = results.length > 0;

  return (
    <div className="bulk-validator">

      {/* ── Upload card ── */}
      <div className="card bv-card">
        <div className="bv-header">
          <div>
            <h2 className="bv-title">Bulk Email Validation</h2>
            <p className="bv-desc">
              Upload a CSV, Excel, or PDF file with email addresses.
              Up to {MAX_EMAILS} emails · max 25 MB.
            </p>
          </div>
          <button className="bv-template-btn" onClick={downloadTemplate} title="Download sample CSV">
            ↓ Template
          </button>
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`bv-dropzone${isDragActive ? ' drag-active' : ''}${file ? ' has-file' : ''}`}
        >
          <input {...getInputProps()} />
          {file ? (
            <div className="bv-file-info">
              <span className="bv-file-icon">
                {file.name.endsWith('.csv') ? '📄' : file.name.endsWith('.pdf') ? '📋' : '📊'}
              </span>
              <div className="bv-file-details">
                <div className="bv-file-name">{file.name}</div>
                <div className="bv-file-meta">
                  {(file.size / 1024).toFixed(1)} KB
                  {preview && preview.emailCount != null &&
                    ` · ~${preview.emailCount.toLocaleString()} email${preview.emailCount !== 1 ? 's' : ''} detected`}
                </div>
              </div>
              {!isRunning && (
                <button
                  className="bv-file-remove"
                  onClick={e => { e.stopPropagation(); handleClear(); }}
                  title="Remove file"
                >✕</button>
              )}
            </div>
          ) : (
            <div className="bv-drop-empty">
              <span className="bv-drop-icon">⬆</span>
              <div className="bv-drop-text">
                {isDragActive ? 'Drop the file here…' : 'Drag & drop a CSV or Excel file'}
              </div>
              <div className="bv-drop-sub">or click to browse</div>
              <div className="bv-drop-formats">.csv · .xlsx · .xls · .pdf · max 25 MB · max {MAX_EMAILS} emails</div>
            </div>
          )}
        </div>

        {/* Preview stats */}
        {preview && !isRunning && (
          <div className="bv-preview-bar">
            <div className="bv-preview-stat">
              <span className="bv-preview-num">{preview.emailCount?.toLocaleString() ?? '—'}</span>
              <span className="bv-preview-label">Unique emails</span>
            </div>
            {preview.totalRows != null && (
              <div className="bv-preview-stat">
                <span className="bv-preview-num">{preview.totalRows.toLocaleString()}</span>
                <span className="bv-preview-label">Total rows</span>
              </div>
            )}
            {preview.duplicateCount != null && (
              <div className="bv-preview-stat bv-preview-stat--warn">
                <span className="bv-preview-num">{preview.duplicateCount.toLocaleString()}</span>
                <span className="bv-preview-label">Duplicates removed</span>
              </div>
            )}
            {preview.emailKey && (
              <div className="bv-preview-stat">
                <span className="bv-preview-num bv-preview-key">"{preview.emailKey}"</span>
                <span className="bv-preview-label">Email column</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="bv-actions">
          {isRunning ? (
            <button className="btn-cancel" onClick={handleCancel}>
              ✕ Cancel
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={handleValidate}
              disabled={!file || phase === 'done'}
            >
              {phase === 'done'
                ? '✓ Validation complete'
                : `Validate${preview?.emailCount ? ` ${preview.emailCount.toLocaleString()} Email${preview.emailCount !== 1 ? 's' : ''}` : ''}`}
            </button>
          )}
          {!isRunning && file && phase !== 'idle' && (
            <button className="btn-ghost" onClick={handleClear}>
              ↺ New validation
            </button>
          )}
        </div>

        {errorMsg && (
          <div className="bv-error">{errorMsg}</div>
        )}
      </div>

      {/* ── Progress bar ── */}
      {(isRunning || (phase === 'done' && progress.total > 0) || phase === 'cancelled') && (
        <div className="bv-progress-card">
          <div className="bv-progress-track">
            <div
              className={`bv-progress-fill${phase === 'cancelled' ? ' cancelled' : phase === 'done' ? ' done' : ''}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="bv-progress-meta">
            <span className="bv-progress-label">
              {phase === 'uploading' && 'Uploading file…'}
              {phase === 'streaming' && `Validating… ${progress.done.toLocaleString()} / ${progress.total.toLocaleString()}`}
              {phase === 'done' && `✓ Completed — ${progress.done.toLocaleString()} emails validated`}
              {phase === 'cancelled' && `⚠ Cancelled — ${progress.done.toLocaleString()} of ${progress.total.toLocaleString()} validated`}
              {phase === 'error' && `Connection lost — ${progress.done.toLocaleString()} results received`}
            </span>
            <span className="bv-progress-pct">{progressPct}%</span>
          </div>
        </div>
      )}

      {/* ── Live results ── */}
      {showResults && (
        <BulkResults
          results={results}
          summary={summary}
          total={progress.total}
          processed={progress.done}
          isLive={isRunning}
          jobId={jobId}
        />
      )}
    </div>
  );
}
